/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import HiddenContent from '../HiddenContent';
import {
  ClearLocationSearchIcon,
  LocationMagnifierTransparent,
  LocationSearchDefaultIcon,
} from '../Icons';
import useStyles from './styles';
//country ISO2 codes to send to Bing country filter
const ALLOWED_COUNTRIES_ISO2 = ['GB', 'IM', 'GG', 'JE'];
const VALID_REGIONS = ['united kingdom', 'jersey', 'guernsey', 'isle of man'];

const getSuggestionPrimaryText = item =>
  item?.name ||
  item?.address?.locality ||
  item?.address?.adminDistrict2 ||
  item?.address?.adminDistrict ||
  item?.address?.postalCode ||
  item?.address?.formattedAddress ||
  '';

const getSuggestionSecondaryText = item => {
  const primary = getSuggestionPrimaryText(item);
  return [
    item?.address?.postalCode,
    item?.address?.adminDistrict2,
    item?.address?.countryRegion,
  ]
    .filter(Boolean)
    .filter(part => part !== primary)
    .join(', ');
};

const LocationAutoSuggest = ({
  placeholder,
  onError,
  onClear,
  setLocationData,
  currentValue = '',
  isFilter = false,
  setLocationError,
  locationHasError = false,
  currentGeoLocation,
  locationData,
  filterShowHide,
  forceMyLocation,
  onManualInput,
  initialValue,
  isVisible,
  disabled,
  readOnly,
  magnifierIconVariant,
  ...rest
}) => {
  const styles = useStyles();
  const locRef = useRef(null);
  // State to track if the autosuggest list is visible
  const [isSuggestionListVisible, setIsSuggestionListVisible] = useState(false);
  const [query, setQuery] = useState('');
  // Only show suggestions when user has actively typed in the input.
  const [userTyped, setUserTyped] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const isSelectionRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);

  const API_KEY = process.env.REACT_APP_BING_MAPS_KEY;
  const BASE = process.env.REACT_APP_BING_BASE;
  const AUTOSUGGEST = `${BASE}${process.env.REACT_APP_BING_AUTOSUGGEST_URL}`;
  const LOCATIONS = `${BASE}${process.env.REACT_APP_BING_LOCATION_DETAILS_URL}`;
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const containerRef = useRef(null);
  // To track if the user is using a keyboard
  const [isKeyboard, setIsKeyboard] = useState(false);
  const MIN_QUERY_LENGTH = 3;
  const DEBOUNCE_MS = 400;

  useEffect(() => {
    setQuery(initialValue || '');
  }, [initialValue]);

  const fetchSuggestions = useCallback(
    async searchText => {
      if (!searchText) {
        setSuggestions([]);
        setIsSuggestionListVisible(false);
        return;
      }
      if (searchText.length < 1) {
        setSuggestions([]);
        return;
      }
      //ensures only latest requests are valid
      if (abortRef.current) {
        abortRef.current.abort();
      }
      //abort controller prevents race conditions and stale responses from updating state during typing
      abortRef.current = new AbortController();
      const signal = abortRef.current.signal;
      //use only ISO2 country codes in the filter
      const url = `${AUTOSUGGEST}?query=${encodeURIComponent(
        searchText
      )}&includeEntityTypes=Address,Business,Place&maxResults=10&key=${API_KEY}`;
      try {
        //Signal cancels previous fetch if user types again
        const res = await fetch(url, { signal });
        const data = await res.json();
        const suggestionList =
          data?.resourceSets?.[0]?.resources?.[0]?.value || [];
        const results = suggestionList.filter(r => {
          const iso = (r?.address?.countryRegionIso2 || '').toUpperCase();
          return iso && ALLOWED_COUNTRIES_ISO2.includes(iso);
        });
        setSuggestions(results);
        // Limit to max 5 suggestions
        const limitedResults = results.slice(0, 5);
        setSuggestions(limitedResults);
        setActiveIndex(-1);
        setIsSuggestionListVisible(limitedResults.length > 0);
        abortRef.current = null;
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        setLocationError?.(true);
        setSuggestions([]);
      }
    },
    [AUTOSUGGEST, API_KEY, setLocationError]
  );

  useEffect(() => {
    if (isSelectionRef.current) return;
    if (!query || query.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setIsSuggestionListVisible(false);
      return;
    }
    // don't auto-show suggestions for initial values unless the user typed
    if (!userTyped) {
      setSuggestions([]);
      setIsSuggestionListVisible(false);
      return;
    }
    //debounce prevents too many requests during typing
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(query);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query, userTyped, fetchSuggestions]);

  const handleSelect = async item => {
    if (isSelectionRef.current) return;
    isSelectionRef.current = true;
    const { point, address, name } = item;
    //coordinates when present
    let latitude = point?.coordinates?.[0] || null;
    let longitude = point?.coordinates?.[1] || null;
    //display the selected item
    const displayItem = getSuggestionPrimaryText(item);
    //when cordinates missing - try Locations endpoint
    if (!latitude || !longitude) {
      try {
        const q = address?.formattedAddress || name || displayItem;
        const res = await fetch(
          `${LOCATIONS}?q=${encodeURIComponent(q)}&maxResults=10&key=${API_KEY}`
        );
        const data = await res.json();
        let resources = data?.resourceSets?.[0]?.resources || [];

        const isValidLocation = r => {
          const country = (r?.address?.countryRegion || '').toLowerCase();
          const admin = (r?.address?.adminDistrict2 || '').toLowerCase();
          return (
            VALID_REGIONS.includes(country) || VALID_REGIONS.includes(admin)
          );
        };

        resources = resources.filter(isValidLocation);
        //Use the first valid geocodePoint
        for (const res of resources) {
          const geoPoint = res?.geocodePoints?.[0];
          if (geoPoint) {
            latitude = geoPoint?.coordinates?.[0];
            longitude = geoPoint?.coordinates?.[1];
            break;
          }
        }
      } catch (err) {
        setLocationError?.(true);
      }
    }
    //UI updation after selecting or geocoding
    setLocationError?.(false);
    setIsSuggestionListVisible(false);
    setSuggestions([]);
    setSelectedItem(displayItem);
    setQuery(displayItem);
    setActiveIndex(-1);

    setLocationData({
      ...(latitude && longitude
        ? {
            latitude,
            longitude,
          }
        : {}),
      readableLocation: displayItem,
      secondaryLocation: getSuggestionSecondaryText(item),
    });

    setTimeout(() => {
      isSelectionRef.current = false;
    }, 300);
  };

  const handleClearInput = () => {
    setQuery('');
    setSelectedItem(false);
    // reset typed flag so dropdown won't show for initial/programmatic values
    setUserTyped(false);
    setLocationData({});
    setIsSuggestionListVisible(false);
    if (onManualInput) {
      onManualInput();
    }
    if (onClear) onClear();
    setLocationError?.(true);
  };

  const handleInputChange = e => {
    const value = e.target.value;
    if (value.length === 0) {
      setLocationError?.(true);
    } else {
      // Input has content - hide error and button
      if (locationHasError) {
        setLocationError?.(false);
        // Clear keyboard focus styling when user starts typing
        setIsKeyboard(false);
      }
    }
    setQuery(value);
    setUserTyped(true);
    if (onManualInput) {
      onManualInput();
    }
  };

  const handleKeyDown = e => {
    if (!suggestions.length) return;
    if (e.key === 'ArrowDown') {
      setActiveIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      setActiveIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter') {
      const selected = suggestions[activeIndex] || suggestions[0];
      if (selected) handleSelect(selected);
    } else if (e.key === 'Tab') {
      setActiveIndex(-1);
    }
  };

  const handleFocus = () => {
    if (locationHasError) {
      setQuery('');
    }
  };

  const handleBlur = () => {
    if (!query || query.trim() === '') {
      setLocationError(true);
    }
  };

  useEffect(() => {
    // when user used a pointer (mouse/touch)
    const handlePointerDown = () => {
      setIsKeyboard(false);
    };
    // Indicating keyboard interaction.
    const handleDocumentKeyDown = e => {
      if (e.key !== 'Tab') return;
      setIsKeyboard(true);
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleDocumentKeyDown, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleDocumentKeyDown, true);
    };
  }, []);

  const isInputReadOnly = Boolean(
    selectedItem || readOnly || query === 'Your location'
  );
  return (
    <div id="searchBoxContainer" css={styles.locSuggesst} ref={containerRef}>
      <div
        className={isKeyboard && isFocused ? 'keyboard-focus' : ''}
        css={styles.inputWrapper(
          magnifierIconVariant,
          isFilter,
          currentValue,
          locationHasError
        )}
        {...rest}
      >
        <div>
          {' '}
          <LocationMagnifierTransparent />{' '}
        </div>
        <input
          type="text"
          id="searchBox"
          autoComplete="off"
          placeholder="Search by area or postcode"
          value={query}
          onChange={isInputReadOnly ? undefined : handleInputChange}
          onKeyDown={handleKeyDown}
          css={styles.input(isFilter, locationHasError)}
          aria-describedby="postcode-description"
          role="combobox"
          aria-controls="suggestion-list"
          aria-expanded={isSuggestionListVisible}
          ref={locRef}
          onFocus={() => {
            setIsFocused(true);
            handleFocus();
          }}
          onBlur={() => {
            setIsFocused(false);
            handleBlur();
            setIsKeyboard(false);
          }}
          onClear={() => {
            handleClearInput();
          }}
          disabled={disabled}
          readOnly={isInputReadOnly}
        />
        {/*  close icon for typing*/}
        {!locationHasError && (query.length > 0 || forceMyLocation) && (
          <span
            onClick={handleClearInput}
            aria-label="Close"
            css={styles.closeBtn}
            tabIndex={0}
            onKeyPress={e => {
              if (e.key === 'Enter') handleClearInput();
              if (e.key === ' ') {
                e.preventDefault();
                handleClearInput();
              }
            }}
          >
            {<ClearLocationSearchIcon />}
          </span>
        )}

        {suggestions.length > 0 && (
          <ul css={styles.searchDropdownList}>
            {suggestions.map((item, i) => {
              const isActive = i === activeIndex;
              const primary = getSuggestionPrimaryText(item);
              const secondary = getSuggestionSecondaryText(item);
              return (
                <li
                  key={i}
                  className={isActive ? 'active' : ''}
                  onMouseDown={() => handleSelect(item)}
                  onClick={() => handleSelect(item)}
                  css={styles.listItems(isActive)}
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSelect(item);
                  }}
                >
                  <>
                    <div css={styles.listItemContent}>
                      <div css={styles.locationSvg}>
                        <LocationSearchDefaultIcon />
                      </div>
                      <div>
                        <div css={styles.primaryText}>{primary}</div>
                        <div css={styles.secondaryText}>{secondary}</div>
                      </div>
                    </div>
                  </>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <HiddenContent id="postcode-description">
        Use the down arrow to select one of the results
      </HiddenContent>
    </div>
  );
};

LocationAutoSuggest.propTypes = {
  placeholder: PropTypes.string,
  onError: PropTypes.func,
  setLocationData: PropTypes.func,
  currentValue: PropTypes.string,
  isFilter: PropTypes.bool,
  setLocationError: PropTypes.func,
  locationHasError: PropTypes.bool,
  currentGeoLocation: PropTypes.object,
  locationData: PropTypes.object,
};
export default LocationAutoSuggest;
